import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Platform, ScrollView, useWindowDimensions } from 'react-native'

import {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet'
import { Pressable, Stack } from '@zet/design-system'
import BrandBenifitsContent from '@zet/feature-brands/screens/brandBenefits/brandBenifitsContent'
import { Button } from '@zet/integration-flow'
import { ScreenTitle } from '@zet/integration-flow/components/screenTitle'
import { M14 } from '@zet/integration-flow/components/typography'
import { i18n } from '@zet/localization'
import BrandBenifits from '@zet/svg/brandBenifits'
import EligibilityCreteria from '@zet/svg/eligibilityCreteria'

import { JFSEligibilityCriteriaTab } from './jfsEligibilityCriteriaTab'

// Remove default borders from react-spring-bottom-sheet
if (typeof document !== 'undefined' && Platform.OS === 'web') {
  const style = document.createElement('style')
  style.textContent = `
    [data-rsbs-header] {
      border-bottom: none !important;
      box-shadow: none !important;
    }
    [data-rsbs-footer] {
      border-top: none !important;
      box-shadow: none !important;
    }
  `
  document.head.appendChild(style)
}

const JFSBrandDetailsBottomSheet = ({
  isBottomSheetVisible,
  setIsBottomSheetVisible,
  navigate,
  brandId,
  isEligible,
  reason,
  criteria,
  sheetName,
  feature,
  tabBarPressEventName,
  alreadyApplied,
  isSourceWeb,
  isInEligible,
  customerId,
  index,
  customerPhoneNumber,
  leadAdded,
  customerName,
  variantId,
  variantName,
  setIsExploreOtherCardsBSVisible,
  eligibleCardsCountExcludingMagnet,
}: {
  isBottomSheetVisible: boolean
  eligibleCardsCountExcludingMagnet?: number
  setIsBottomSheetVisible: React.Dispatch<React.SetStateAction<boolean>>
  setIsExploreOtherCardsBSVisible?: React.Dispatch<
    React.SetStateAction<boolean>
  >
  navigate: unknown
  brandId: string | number
  isEligible?: boolean
  reason?: string
  criteria?: string
  showShareButton?: boolean
  viewBrandDetailsButton?: boolean
  sheetName?: string
  feature?: string
  tabBarPressEventName?: string
  alreadyApplied?: boolean
  isSourceWeb?: boolean
  isInEligible?: boolean
  customerId?: number
  index: number
  customerPhoneNumber?: number | string
  leadAdded?: boolean
  customerName?: string
  variantId?: string | number
  variantName?: string
  snapPoints?: any
}) => {
  const { height } = useWindowDimensions()
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const [WebBottomSheet, setWebBottomSheet] = useState<any>(null)

  const topBar = [
    {
      id: 'bar-1',
      name: 'Benefits',
      icon: <BrandBenifits isIconSmall />,
      isActive: true,
      renderTab: (
        <BrandBenifitsContent
          brandId={brandId}
          isFixedHeight={false}
          variantId={variantId}
        />
      ),
    },
    {
      id: 'bar-2',
      name: 'Eligibility Criteria',
      icon: <EligibilityCreteria isIconSmall />,
      isActive: false,
      renderTab: (
        <JFSEligibilityCriteriaTab
          brandId={brandId}
          variantId={variantId}
          onGotItPress={() => setIsBottomSheetVisible(false)}
        />
      ),
    },
  ]

  const [isActiveBars, setIsActiveBars] = useState(topBar)

  const handleDismiss = useCallback(() => {
    if (Platform.OS !== 'web') {
      bottomSheetModalRef.current?.dismiss()
    }
    setIsBottomSheetVisible(false)
  }, [setIsBottomSheetVisible])

  // ? Handle active tab of bottom sheet
  const handlePress = (idx: string) => {
    setIsActiveBars([
      ...isActiveBars.map((value) => {
        return { ...value, isActive: value.id === idx }
      }),
    ])
  }

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
      />
    ),
    [],
  )

  // Dynamically load web bottom sheet component
  useEffect(() => {
    if (Platform.OS === 'web' && !WebBottomSheet) {
      Promise.all([
        import('react-spring-bottom-sheet'),
        // @ts-ignore - CSS import for styling
        import('react-spring-bottom-sheet/dist/style.css').catch(() => null),
      ]).then(([module]) => {
        setWebBottomSheet(() => module.BottomSheet)
      })
    }
  }, [WebBottomSheet])

  // Handle visibility changes
  useEffect(() => {
    if (isBottomSheetVisible) {
      if (Platform.OS !== 'web') {
        bottomSheetModalRef.current?.present()
      }
    } else {
      if (Platform.OS !== 'web') {
        bottomSheetModalRef.current?.dismiss()
      }
    }
  }, [isBottomSheetVisible])

  const Header = () => (
    <Stack>
      <ScreenTitle
        title={variantName ?? ''}
        // containerStyle={{ paddingHorizontal: 16 }}
        textStyleOverride={{ fontSize: 14, fontFamily: 'JioType-Bold' }}
        onCrossPress={handleDismiss}
      />
      <ScrollView
        className="mr-2 bg-white"
        horizontal={true}
        showsHorizontalScrollIndicator={false}
      >
        {isActiveBars.map((value) => (
          <Pressable
            key={value.id}
            className="active:bg-grays-10 rounded-lg"
            onPress={() => handlePress(value.id)}
            eventName="click_action"
            eventPayload={{
              item_name: tabBarPressEventName
                ? tabBarPressEventName
                : 'recommendation_app_flow_selected_tab',
              filter_name: value.name.replace('\n', ''),
            }}
          >
            <M14
              className={`${
                value.isActive && 'border-secondary-100 border-b-2'
              } w-100  text-[${value.isActive ? '#1C1D1F' : '#24262BA6'}] mr-4 py-2 `}
              numberOfLines={1}
            >
              {value.name}
            </M14>
          </Pressable>
        ))}
      </ScrollView>
    </Stack>
  )

  const Content = () => (
    <Stack className="bg-grays-20 mt-2">
      {isActiveBars.find((value) => value.isActive)?.renderTab}
    </Stack>
  )

  const Footer = () => {
    return (
      <Stack
        className="bg-white"
        style={{ marginHorizontal: -12, marginTop: -8, marginBottom: -16 }}
      >
        <Button title={i18n.t('gotIt')} onPress={handleDismiss} />
      </Stack>
    )
  }

  // Web implementation
  if (Platform.OS === 'web') {
    if (!WebBottomSheet) {
      return null // Component not loaded yet
    }

    return (
      <WebBottomSheet
        open={isBottomSheetVisible}
        onDismiss={handleDismiss}
        snapPoints={({
          maxHeight,
          minHeight,
        }: {
          maxHeight: number
          minHeight: number
        }) => {
          const contentHeight = minHeight
          const maxAllowed = maxHeight * 0.9
          return [Math.min(contentHeight, maxAllowed)]
        }}
        header={<Header />}
        footer={<Footer />}
      >
        <Content />
      </WebBottomSheet>
    )
  }

  // Mobile implementation
  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      enableDynamicSizing
      maxDynamicContentSize={height * 0.9}
      backdropComponent={renderBackdrop}
      onDismiss={handleDismiss}
      enablePanDownToClose={true}
    >
      <Header />
      <BottomSheetScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 20 }}
      >
        <Content />
      </BottomSheetScrollView>
      <Footer />
    </BottomSheetModal>
  )
}

export default JFSBrandDetailsBottomSheet
